// One result: a model's replay of a task. Its score and costs; the grade (the rubric's criteria with their evidence, the
// counted items, how the synthetic environment bore on the run); the judge's comparisons with the other models; the
// replay as one timeline (each message the user sent, what the candidate did, where the environment agent answered a
// call, the simulated user's decision after each run); the changes; the runs and settings.
import { state, esc, get, renderMd } from '../core'
import { ghDiff, scoreBar, scoreColor } from '../task-replay'
import { renderTranscript, wireTranscript } from '../transcript'
import { crumbs, wireCrumbs } from './crumbs'
import { goReplay, openDataset } from './index'
import { duration, modelName, money, typeName } from './fmt'
import { compact } from './compact'
import { chip, dot, onThisPage, pageHead, typeChip, wireToc } from './head'
import { historyMount, wireHistory } from './history'
import { modelColors } from './pareto'
import { prose, wireProse } from './prose'
import { wireTips } from './tip'

type Any = Record<string, any>

const cache = new Map<string, { result: Any; task: Any | null }>()

export function renderResult(root: HTMLElement, run: string, task: string, model: string): void {
  const key = run + '\n' + task + '\n' + model
  const hit = cache.get(key)
  if (hit) { paint(root, hit.result, hit.task); return }
  root.innerHTML = '<div class="empty">Loading the result…</div>'
  const q = '?run=' + encodeURIComponent(run) + '&task=' + encodeURIComponent(task) + '&model=' + encodeURIComponent(model)
  Promise.all([get('/api/replay/result' + q), get('/api/replay/tasks/' + encodeURIComponent(task)).catch(() => null)])
    .then(([r, t]: any[]) => {
      if (!r || r.error) throw new Error(r?.error || 'no result')
      const tk = t && !t.error ? t : null
      cache.set(key, { result: r, task: tk })
      if (state.rp.run === run && state.rp.task === task && state.rp.model === model) paint(root, r, tk)
    })
    .catch(() => { root.innerHTML = '<div class="empty">Could not load this result.</div>' })
}

function paint(root: HTMLElement, r: Any, t: Any | null): void {
  const d: Any = r.detail || {}
  const g: Any = d.grade || {}
  const tile = (k: string, v: string, sub = '') => '<div class="rp-tile"><div class="rp-tile-k">' + esc(k) + '</div><div class="rp-tile-v">' + v + '</div>' +
    (sub ? '<div class="rp-tile-s">' + sub + '</div>' : '') + '</div>'
  const ds = state.rp.id || r.suiteId || ''
  const trail = [
    { label: 'Datasets', go: () => openDataset('') },
    ...(ds ? [{ label: r.suiteName || 'Dataset', go: () => openDataset(ds) }, { label: 'Tasks', go: () => openDataset(ds, 'rpTasks') }] : []),
    { label: r.title || r.taskId, go: () => goReplay({ id: ds, run: '', model: '' }) },
  ]
  const color = modelColors([r.model, ...r.comparisons.map((c: Any) => c.other)]).get(r.model)
  const score = r.score == null ? '' : '<span class="rp-score" style="background:' + scoreColor(r.score) + '">' + Math.round(r.score * 100) +
    (r.verdict ? '<span>' + esc(r.verdict) + '</span>' : '') + '</span>'
  const head = pageHead({
    trail, mark: dot(color), title: modelName(r.model) + ' on ' + (r.title || r.taskId),
    chips: typeChip(r.taskType) + chip(r.complexity),
    facts: [esc(r.agent), esc(r.repo), 'run ' + esc(r.runLabel || r.runId.slice(0, 8))],
    actions: score,
  })
  const tiles = '<div class="rp-tiles rp-tiles-wide">' +
    tile('Score', scoreBar(r.score)) + tile('Verdict', esc(r.verdict || (r.status === 'ok' ? '—' : r.status))) +
    tile('Time', duration(r.seconds)) + tile('Agent runs', String((d.runs || []).length)) +
    tile('Cost', money(r.cost.agent), 'grading ' + money(r.cost.grade) + ' · replay agents ' + money(r.cost.harness)) + '</div>'
  const sections: [string, string][] = [['rpGrade', 'Grade'], ...(r.comparisons.length ? [['rpJudge', 'Judge']] as [string, string][] : []),
    ['rpReplay', 'Replay'], ['rpChanges', 'Changes'], ['rpRuns', 'Runs']]
  const others: string[] = (d.settings || {}).other_models || []
  root.innerHTML = '<section class="tr-l0 rp-result">' + head + onThisPage(sections) + tiles +
    (r.stoppedReason ? '<div class="notice rp-stop">' + esc(r.stoppedReason) + '</div>' : '') +
    (others.length ? '<div class="notice rp-stop">Some calls went to ' + esc(others.map(modelName).join(', ')) + ', not ' + esc(modelName(r.model)) + '.</div>' : '') +
    block('Grade', g.rubric ? esc(g.rubric) + ' rubric · ' + esc(modelName(g.model || '')) + (g.turns ? ' · ' + g.turns + ' turns' : '') : '', grade(g, r.checks || [], t), 'rpGrade') +
    (r.comparisons.length ? block('Judge', r.comparisons.length + ' comparisons', comparisons(r.comparisons), 'rpJudge') : '') +
    block('Replay', replaySub(d), '<div class="tr-card rp-tx rp-scroll rp-scroll-tall">' + (before(t) ? historyMount() : '') + timeline(r.transcript || [], d) + '</div>', 'rpReplay') +
    block('Changes', esc(r.diffStat || ''), r.diff ? '<div class="tr-card rp-scroll rp-scroll-tall">' + ghDiff(r.diff) + '</div>' : '<div class="empty">No changes.</div>', 'rpChanges') +
    block('Runs and settings', '', settings(d), 'rpRuns') +
    '</section>'
  wireCrumbs(root, trail)
  wireToc(root)
  wireTranscript(root.querySelector('.rp-tx') as HTMLElement)
  wireHistory(root, r.taskId, false)
  wireProse(root)
  wireTips(root)
}

/** Whether the task has a conversation before it: a main-thread task that does not start its session. */
const before = (t: Any | null) => !!t && (!t.span.agent || t.span.agent === 'main') && (t.span.lo ?? 0) > 0

const block = (title: string, sub: string, body: string, id = '') =>
  '<div class="tr-block"' + (id ? ' id="' + id + '"' : '') + '><div class="tr-block-head"><h3>' + esc(title) + '</h3>' +
  (sub ? '<span class="tr-sub">' + sub + '</span>' : '') + '</div>' + body + '</div>'

/** The gates (verify.py), as a reader says them: what each found true, or not. */
const GATE: Record<string, string> = {
  patch_nonempty: 'changed files', files_touched: 'touched the reference’s files', output_present: 'wrote an answer',
  tests_p2p: 'existing tests still pass', tests_fail_on_base: 'new tests fail before the change',
  'tests_fail_on_base:real': 'new tests fail before the change', 'tests_fail_on_base:decorative': 'new tests also pass without the change',
  'tests_fail_on_base:no_tests': 'wrote no tests where the reference has some',
}

const pill = (text: string, tone: 'good' | 'mid' | 'bad' | 'none') => '<span class="rp-pill rp-pill-' + tone + '">' + esc(text) + '</span>'

/** Each criterion's labels, best first (eval/taskreplay/replay/rubrics.py RUBRICS): the first reads good, the last
 *  bad, the rest in between. */
const LABELS: Record<string, string[]> = {
  correctness: ["correct", "minor_issues", "major_defect", "wrong_or_broken"],
  completeness: ["complete", "minor_gaps", "major_gaps", "mostly_missing"],
  tests: ["meaningful", "shallow", "missing"],
  design: ["sound", "acceptable", "poor"],
  scope: ["focused", "minor_extras", "unrequested_work"],
  fix_quality: ["root_cause_fixed", "fixed_narrowly", "symptom_masked", "not_fixed"],
  diagnosis: ["correct", "partially_correct", "wrong_or_absent"],
  regression_test: ["pins_the_bug", "weak", "missing"],
  root_cause: ["same_root_cause", "right_area_imprecise", "partially_right", "wrong_or_missing"],
  evidence: ["well_supported", "partly_supported", "unsupported"],
  fix_direction: ["viable", "vague", "wrong"],
  targets_right_behavior: ["same_behaviors", "most", "some", "wrong_target"],
  assertion_strength: ["strong", "mostly_strong", "weak", "vacuous"],
  case_coverage: ["matches_or_exceeds", "minor_gaps", "major_gaps", "happy_path_only"],
  reliability: ["stable", "minor_risk", "flaky_or_env_dependent"],
  requirements_coverage: ["all", "most", "some", "few"],
  approach_soundness: ["sound", "sound_with_gaps", "questionable", "unworkable"],
  grounded_in_codebase: ["grounded", "partly_grounded", "generic", "wrong_about_code"],
  risks_and_edge_cases: ["thorough", "adequate", "thin", "absent"],
  actionability: ["ready_to_implement", "needs_detail", "vague"],
  conclusions: ["same_conclusions", "mostly", "partly", "different_or_missing"],
  support: ["well_supported", "partly_supported", "unsupported"],
  key_points: ["all", "most", "some", "few_or_none"],
  factual_accuracy: ["accurate", "minor_inaccuracy", "fabrication"],
  fit_for_purpose: ["ready_to_send", "needs_light_edit", "needs_rewrite"],
  verdict_match: ["same", "adjacent", "opposite"],
  specificity: ["precise", "general", "vague"],
}
/** The counted items' verdicts and the environment's. */
const VERDICT_TONE: Record<string, 'good' | 'mid' | 'bad'> = {
  matched: 'good', valid: 'good', trivial: 'mid', unverifiable: 'mid', invalid: 'bad', wrong: 'bad',
  no: 'good', worked_around: 'mid', blocked: 'bad',
}
/** The grader's verdict on whether the synthetic environment (playback, refusals, what the sandbox lacks) bore on the
 *  run, as a reader says it (rubrics.py ENVIRONMENT). */
const ENV_LABEL: Record<string, string> = { no: 'no effect on the run', worked_around: 'worked around it', blocked: 'blocked the run' }
/** The counted items' verdicts on the candidate's own claims or findings, as a reader says them (rubrics.py). */
const ITEM_LABEL: Record<string, string> = {
  matched: 'in the reference', valid: 'right, not in the reference', trivial: 'minor', invalid: 'wrong',
  unverifiable: "can't be checked", wrong: 'wrong',
}

function tone(label: string, criterion = ''): 'good' | 'mid' | 'bad' | 'none' {
  const order = LABELS[criterion]
  const i = order ? order.indexOf(label) : -1
  if (i >= 0) return i === 0 ? 'good' : i === order!.length - 1 ? 'bad' : 'mid'
  return VERDICT_TONE[label] ?? 'none'
}
const words = (s: string) => s.replace(/_/g, ' ')

/** The grader's answer: criteria, the counted items beside the reference's own, the environment, the analysis. */
function grade(g: Any, checks: Any[], t: Any | null): string {
  if (!g.answer) return '<div class="empty">' + esc(g.error || 'Not graded.') + '</div>'
  const a: Any = g.answer
  const ref: Any = t?.reference || {}
  const itemText = (id: string): string => {
    const i = Number(id.slice(1)) - 1
    const item = id[0] === 'C' ? (ref.claims || [])[i] : (ref.findings || [])[i]
    return item ? String(item.claim || item.finding || item.title || item.summary || item.text || JSON.stringify(item)) : ''
  }
  let out = ''
  const gates = checks.filter((c) => !c.informational && c.kind === 'deterministic')
  if (gates.length) {
    out += '<div class="rp-gates">' + gates.map((c) => '<span class="rp-gate' + (c.passed ? '' : ' fail') + '" data-tip="' +
      esc((GATE[c.name] || words(c.name)) + '\n' + (c.detail || '')) + '">' + (c.passed ? '✓ ' : '✗ ') + esc(GATE[c.name] || words(c.name)) + '</span>').join('') + '</div>'
  }
  if (a.criteria && typeof a.criteria === 'object') {
    out += '<div class="tr-card tr-card-flat"><table class="tr-lb2 rp-crit"><thead><tr><th>Criterion</th><th>Label</th><th>Evidence</th></tr></thead><tbody>' +
      Object.entries(a.criteria as Any).map(([k, v]: [string, Any]) => '<tr><td class="rp-crit-k">' + esc(words(k)) + '</td><td>' +
        (v.label ? pill(words(v.label), tone(v.label, k)) : '—') + '</td><td class="rp-ev">' + prose(v.evidence) + '</td></tr>').join('') +
      '</tbody></table></div>'
  }
  for (const [k, v] of Object.entries(a)) {
    if (!Array.isArray(v) || !v.length || typeof v[0] !== 'object') continue
    if ('found' in v[0]) {
      const found = v.filter((x: Any) => x.found).length
      out += '<div class="rp-count-h">' + esc(words(k)) + ' · <b>' + found + ' of ' + v.length + '</b> found</div>' +
        '<div class="tr-card tr-card-flat"><table class="tr-lb2 rp-crit"><tbody>' + v.map((x: Any) => '<tr><td class="rp-crit-k mono">' + esc(x.id) + '</td>' +
          '<td>' + pill(x.found ? (x.valid === false ? 'found, wrong' : 'found') : 'missed', x.found ? (x.valid === false ? 'mid' : 'good') : 'bad') + '</td>' +
          '<td class="rp-ev">' + (itemText(x.id) ? '<div class="rp-item">' + esc(itemText(x.id)) + '</div>' : '') + prose(x.evidence) + '</td></tr>').join('') +
        '</tbody></table></div>'
    } else if ('verdict' in v[0]) {
      const what = Object.keys(v[0]).find((f) => ['evidence', 'verdict', 'matches'].indexOf(f) < 0) || ''
      const by = new Map<string, number>()
      for (const x of v) by.set(x.verdict, (by.get(x.verdict) ?? 0) + 1)
      out += '<div class="rp-count-h">' + esc(words(k)) + ' · ' + [...by.entries()].map(([vv, n]) => n + ' ' + esc(ITEM_LABEL[vv] || words(vv))).join(', ') + '</div>' +
        '<div class="tr-card tr-card-flat"><table class="tr-lb2 rp-crit"><tbody>' + v.map((x: Any) => '<tr><td>' + pill(ITEM_LABEL[x.verdict] || words(x.verdict), tone(x.verdict)) +
          (x.matches ? ' <span class="mono tr-muted">' + esc(x.matches) + '</span>' : '') + '</td>' +
          '<td class="rp-ev"><div class="rp-item">' + esc(String(x[what] ?? '')) + '</div>' + prose(x.evidence) + '</td></tr>').join('') +
        '</tbody></table></div>'
    }
  }
  if (a.environment) {
    const e = a.environment
    out += '<div class="rp-count-h">Environment · ' + pill(ENV_LABEL[e.verdict] || words(e.verdict || '—'), tone(e.verdict)) + '</div>' +
      '<div class="rp-ev rp-ev-block">' + prose(e.evidence) + ((e.gaps || []).length ? '<ul>' + e.gaps.map((x: string) => '<li>' + esc(x) + '</li>').join('') + '</ul>' : '') + '</div>'
  }
  if (a.analysis) out += '<div class="rp-count-h">Analysis</div><div class="rp-ev rp-ev-block">' + prose(a.analysis) + '</div>'
  return out
}

const PREF: Record<string, [string, 'good' | 'mid' | 'bad' | 'none']> = {
  clearly: ['clearly better', 'good'], slightly: ['slightly better', 'good'], tie: ['tie', 'none'],
  slightly_other: ['slightly worse', 'mid'], clearly_other: ['clearly worse', 'bad'],
}

function comparisons(list: Any[]): string {
  return '<div class="tr-card tr-card-flat"><table class="tr-lb2 rp-crit"><thead><tr><th>Against</th><th>This one</th><th>Judge</th><th>Why</th></tr></thead><tbody>' +
    list.map((c) => {
      const [text, t] = PREF[c.preference] ?? ['—', 'none']
      const crit = c.criteria ? Object.entries(c.criteria as Any).map(([k, v]: [string, Any]) =>
        '<div><b>' + esc(words(k)) + '</b>: ' + esc(v.evidence || v.preference || '') + '</div>').join('') : ''
      return '<tr><td>' + esc(modelName(c.other)) + '</td><td><span data-tip="' + esc('this result was ' + c.side + ' in the judge’s reasoning') + '">' + pill(text, t) + '</span></td><td>' + esc(modelName(c.judgeModel)) + '</td><td class="rp-ev">' +
        '<details><summary>' + esc(String(c.analysis || '').slice(0, 220)) + (String(c.analysis || '').length > 220 ? '…' : '') + '</summary>' +
        '<div class="tr-prose rp-prose">' + renderMd(String(c.analysis || '')) + '</div>' + crit + '</details></td></tr>'
    }).join('') + '</tbody></table></div>'
}

function replaySub(d: Any): string {
  const sim = (d.simulator || []) as Any[]
  const env = (d.environment || []) as Any[]
  const count = (xs: Any[], key: string, v: string) => xs.filter((x) => x[key] === v).length
  const sent = count(sim, 'action', 'send') + count(sim, 'action', 'rewrite') + count(sim, 'action', 'answer')
  const parts = [
    ((d.runs || []) as Any[]).length + ' agent run' + (((d.runs || []) as Any[]).length === 1 ? '' : 's'),
    sent ? sent + ' message' + (sent === 1 ? '' : 's') + ' sent' : '',
    count(sim, 'action', 'skip') ? count(sim, 'action', 'skip') + ' skipped' : '',
    count(env, 'decision', 'respond') ? count(env, 'decision', 'respond') + ' calls played back' : '',
    count(env, 'decision', 'related') ? count(env, 'decision', 'related') + ' related' : '',
    count(env, 'decision', 'no_access') ? count(env, 'decision', 'no_access') + ' refused' : '',
  ].filter(Boolean)
  return esc(parts.join(' · '))
}

/** The transcript cut at each message sent, with the simulated user's decisions between runs. */
function timeline(tx: Any[], d: Any): string {
  const env = new Map<string, Any>()
  for (const e of (d.environment || []) as Any[]) if (e.decision && e.decision !== 'run' && e.command) env.set(String(e.command).trim(), e)
  const note = (name: string, input: unknown): string => {
    const i = (input && typeof input === 'object' ? input : {}) as Any
    const cmd = name === 'Bash' ? String(i.command || '').trim() : name === 'Read' && i.file_path ? 'Read ' + String(i.file_path) : ''
    const e = cmd ? env.get(cmd) : undefined
    if (!e) return ''
    const label = e.decision === 'respond' ? 'played back' : e.decision === 'no_access' ? 'no access' : e.decision
    return '<span class="rp-env rp-env-' + esc(e.decision) + '" data-tip="' + esc('environment: ' + label + '\n' + (e.why || '')) + '">' + esc(label) + '</span>'
  }
  const segments: Any[][] = []
  for (const m of tx) {
    if (m.role === 'user' || !segments.length) segments.push([])
    segments[segments.length - 1]!.push(m)
  }
  const decisions = [...((d.simulator || []) as Any[])]
  let out = ''
  segments.forEach((seg, i) => {
    const [first, ...rest] = seg[0]?.role === 'user' ? seg : [null, ...seg]
    if (first) out += '<div class="rp-msg"><div class="rp-msg-h">' + (i === 0 ? 'Task' : 'User') + '</div><div class="rp-msg-b">' + esc(String(first.text || '')) + '</div></div>'
    out += renderTranscript(compact(rest), note)
    while (decisions.length) {
      const s = decisions.shift()!
      out += decision(s)
      if (s.action !== 'skip') break
    }
  })
  return out + decisions.map(decision).join('')
}

function decision(s: Any): string {
  const what: Record<string, string> = { send: 'sends the next message', rewrite: 'rewrites the next message', skip: 'skips a message',
    answer: 'answers the agent', end: 'ends the session' }
  return '<div class="rp-sim rp-sim-' + esc(s.action) + '"><span class="rp-sim-a">Simulated user ' + esc(what[s.action] || s.action) + '</span>' +
    (s.why ? '<span class="rp-sim-w">' + esc(s.why) + '</span>' : '') + '</div>'
}

function settings(d: Any): string {
  const runs = (d.runs || []) as Any[]
  const s: Any = d.settings || {}
  const comp = s.compaction
  const named = runs.some((r) => (r.models || []).length)
  const rows = runs.map((r, i) => '<tr><td class="mono">' + (i + 1) + '</td><td class="r">' + duration(r.seconds) + '</td><td class="r">' + money(r.cost_usd) +
    '</td><td class="r">' + (r.api_calls ?? '—') + '</td><td class="r">' + (r.tokens != null ? Number(r.tokens).toLocaleString('en-US') : '—') + '</td>' +
    (named ? '<td>' + esc((r.models || []).map(modelName).join(', ')) + '</td>' : '') + '<td>' +
    esc(r.is_error ? 'error' : r.result || '') + '</td></tr>').join('')
  const facts = [
    ['Effort', s.effort || 'default'], ['History', s.history_mode || '—'],
    ['Compaction', comp?.compacted ? Number(comp.tokens).toLocaleString('en-US') + ' tokens compacted' : 'none'],
    ['Test baseline', s.baseline ? 'yes' : 'no'],
    ...Object.entries((d.settings?.support_models || d.support_models || {}) as Any).map(([k, v]) => [k, modelName(String(v))]),
  ]
  return '<div class="tr-card tr-card-flat"><table class="tr-runs"><thead><tr><th>Run</th><th class="r">Time</th><th class="r">Cost</th>' +
    '<th class="r">API calls</th><th class="r">Tokens</th>' + (named ? '<th>Models</th>' : '') + '<th>Result</th></tr></thead><tbody>' + rows + '</tbody></table></div>' +
    '<div class="rp-facts">' + facts.map(([k, v]) => '<span><span class="tr-muted">' + esc(String(k)) + '</span> ' + esc(String(v)) + '</span>').join('') + '</div>'
}

